import { useCallback, useEffect, useMemo } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import Group from 'components/utils/Group'
import CloseButton from 'components/Common/CloseButton'
import NestableClickable from 'components/Common/NestableClickable'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getDestinationImage, isDestinationSearchItem } from 'lib/search/searchUtils'
import { useInView } from 'react-intersection-observer'

import type { ExperiencesHomepageSuggestedSearchView } from 'lib/experiences/experiencesHomepageUtils'

import { useExperiencesHomepageUpliftTracking } from './experiencesHomepageUpliftAnalytics'

const IMAGE_WIDTH = 68

export const EXPERIENCES_HOMEPAGE_RECENT_SEARCH_TILE = {
  widthMobile: 300,
  widthDesktop: 366,
  heightMobile: 88,
  heightDesktop: 92,
} as const

const TILE_HEIGHT_MOBILE = EXPERIENCES_HOMEPAGE_RECENT_SEARCH_TILE.heightMobile
const TILE_WIDTH_MOBILE = EXPERIENCES_HOMEPAGE_RECENT_SEARCH_TILE.widthMobile
const TILE_HEIGHT_DESKTOP = EXPERIENCES_HOMEPAGE_RECENT_SEARCH_TILE.heightDesktop
const TILE_WIDTH_DESKTOP = EXPERIENCES_HOMEPAGE_RECENT_SEARCH_TILE.widthDesktop

const TextContainer = styled(Group)`
  padding-block: ${rem(8)};
  padding-inline: ${rem(16)};
  display: flex;
  min-inline-size: 0;
  flex: 1 1 auto;
`

const ImageBlock = styled(Group)`
  flex-shrink: 0;
  inline-size: ${rem(IMAGE_WIDTH)};
  block-size: ${rem(TILE_HEIGHT_MOBILE)};

  ${mediaQueryUp.tablet} {
    block-size: ${rem(TILE_HEIGHT_DESKTOP)};
  }
`

const IconBlock = styled(Group)`
  inline-size: ${rem(IMAGE_WIDTH)};
  block-size: ${rem(TILE_HEIGHT_MOBILE)};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.tablet} {
    block-size: ${rem(TILE_HEIGHT_DESKTOP)};
  }
`

const Root = styled(NestableClickable)`
  align-self: start;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  overflow: hidden;
  transition: outline 0.2s, background-color 0.2s;
  inline-size: ${rem(TILE_WIDTH_MOBILE)};
  block-size: ${rem(TILE_HEIGHT_MOBILE)};

  ${mediaQueryUp.tablet} {
    inline-size: ${rem(TILE_WIDTH_DESKTOP)};
    block-size: ${rem(TILE_HEIGHT_DESKTOP)};

    .close-button {
      transition: opacity 0.2s;
      opacity: 0;
      pointer-events: none;
    }

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

        .close-button {
          pointer-events: auto;
          opacity: 1;
        }
      }
    }
  }
`

interface Props {
  item: ExperiencesHomepageSuggestedSearchView
  position: number
  onDismiss: (item: ExperiencesHomepageSuggestedSearchView) => void
}

function ExperiencesHomepageRecentSearchTile({ item, position, onDismiss }: Props) {
  const intl = useIntl()
  const { recentSearch, title, primaryCaption, secondaryCaption, to } = item
  const [trackingRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })
  const trackingSubject = item.source === 'experience'
    ? 'suggested_searches_experience'
    : 'suggested_searches_global'
  const trackInteraction = useExperiencesHomepageUpliftTracking(trackingSubject)

  useEffect(() => {
    if (inView) {
      trackInteraction(trackingSubject, 'impression', position)
    }
  }, [inView, position, trackInteraction, trackingSubject])

  const handleClick = useCallback(() => {
    trackInteraction(trackingSubject, 'tile_clicked', position)
  }, [position, trackInteraction, trackingSubject])

  const handleDismissed = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    trackInteraction(trackingSubject, 'tile_dismissed', position)
    onDismiss(item)
  }, [item, onDismiss, position, trackInteraction, trackingSubject])

  const displayImageId = useMemo(() => {
    const { searchItem } = recentSearch
    if (searchItem.imageId) {
      return searchItem.imageId
    }

    if (searchItem.searchType === 'searchItemV3' && searchItem.display.imageId) {
      return searchItem.display.imageId
    }

    if (isDestinationSearchItem(searchItem)) {
      return getDestinationImage(searchItem.value)
    }

    if (
      searchItem.searchType === 'searchItemV3'
      && 'placeIds' in searchItem.searchItem.extractedData
      && searchItem.searchItem.extractedData.placeIds?.length
    ) {
      return getDestinationImage(searchItem.searchItem.extractedData.placeIds[0])
    }

    return undefined
  }, [recentSearch])

  return (
    <Root to={to} onClick={handleClick} ref={trackingRef}>
      <Group direction="horizontal" verticalAlign="stretch" fullHeight>
        {displayImageId && (
          <ImageBlock direction="vertical">
            <Image
              fit="center"
              width={IMAGE_WIDTH}
              height={TILE_HEIGHT_DESKTOP}
              dpr={2}
              id={displayImageId}
              loading="lazy"
            />
          </ImageBlock>
        )}
        {!displayImageId && (
          <IconBlock direction="vertical" horizontalAlign="center" verticalAlign="center">
            <LineHotAirBalloonIcon style={{ width: 32, height: 32 }} />
          </IconBlock>
        )}
        <Group direction="horizontal" verticalAlign="space-between" fullWidth>
          <TextContainer direction="vertical" verticalAlign="center">
            <BodyText variant="medium" weight="bold" lineClamp={1}>{title}</BodyText>
            {!!primaryCaption && (
              <Caption variant="large" lineClamp={1}>{primaryCaption}</Caption>
            )}
            {!!secondaryCaption && (
              <Caption variant="large" lineClamp={1}>{secondaryCaption}</Caption>
            )}
          </TextContainer>
          <CloseButton
            onClick={handleDismissed}
            className="close-button"
            aria-label={intl.formatMessage({
              id: 'xESE_NX_R1p70pQEK8dv',
              defaultMessage: 'Dismiss suggested search',
              description: 'Accessible label for dismissing a suggested search tile on Experiences homepage uplift',
            })}
          />
        </Group>
      </Group>
    </Root>
  )
}

export default ExperiencesHomepageRecentSearchTile
